/**
 * AI Insights Cards
 *
 * Displays AI-powered recommendations based on emissions data.
 * Following CarbonIQ Analytics design with success/warning/info variants.
 */


import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';

type InsightVariant = 'success' | 'warning' | 'info';

interface AIInsight {
  id: string;
  title: string;
  description: string;
  variant: InsightVariant;
}

interface AIInsightsCardsProps {
  isLoading?: boolean;
}

const variantConfig: Record<InsightVariant, { bgColor: string; iconColor: string; icon: string }> = {
  success: {
    bgColor: 'rgba(40, 167, 69, 0.1)',
    iconColor: '#28a745',
    icon: 'fa-circle-check',
  },
  warning: {
    bgColor: 'rgba(255, 193, 7, 0.1)',
    iconColor: '#ffc107',
    icon: 'fa-triangle-exclamation',
  },
  info: {
    bgColor: 'rgba(0, 123, 255, 0.1)',
    iconColor: '#007bff',
    icon: 'fa-lightbulb',
  },
};

// Mock insights - would come from API/AI service in production
const mockInsights: AIInsight[] = [
  {
    id: '1',
    title: 'Strong Progress on Scope 2',
    description:
      'Your electricity emissions decreased significantly this quarter due to renewable energy procurement. Consider expanding this strategy to other facilities.',
    variant: 'success',
  },
  {
    id: '2',
    title: 'Scope 1 Attention Needed',
    description:
      'Fleet vehicle emissions increased compared to last quarter. Review transportation patterns and consider transitioning to electric vehicles.',
    variant: 'warning',
  },
  {
    id: '3',
    title: 'On Track for Reduction Target',
    description:
      'At current reduction rate, you are making good progress toward your emissions reduction target. Maintain focus on energy efficiency to secure this trajectory.',
    variant: 'info',
  },
];

export const AIInsightsCards = ({ isLoading }: AIInsightsCardsProps) => {
  if (isLoading) {
    return (
      <Card>
        <CardContent className='tw:p-4'>
          <div className='tw:flex tw:items-center tw:justify-center tw:py-4'>
            <div className='tw:size-4 tw:border-2 tw:border-emerald-500 tw:border-t-transparent tw:rounded-full tw:animate-spin tw:mr-2' />
            <span className='tw:text-muted-foreground'>Generating insights...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className='tw:p-4'>
        <div className='tw:mb-3'>
          <h5 className='tw:mb-1 tw:font-semibold'>
            <i className='fa fa-wand-magic-sparkles tw:mr-2 tw:text-blue-500' />
            AI-Powered Insights <Badge variant='default'>Coming Soon</Badge>
          </h5>
          <p className='tw:text-muted-foreground tw:mb-0 tw:text-sm'>Automated recommendations based on your emissions data</p>
        </div>

        <div className='tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-3'>
          {mockInsights.map((insight) => {
            const config = variantConfig[insight.variant];
            return (
              <div
                key={insight.id}
                className='tw:p-3 tw:rounded tw:h-full'
                style={{ backgroundColor: 'rgba(248, 249, 250, 0.5)' }}
              >
                <div className='tw:flex tw:items-start'>
                  <div
                    className='tw:rounded-full tw:flex tw:items-center tw:justify-center tw:mr-3 tw:shrink-0'
                    style={{
                      width: 36,
                      height: 36,
                      backgroundColor: config.bgColor,
                    }}
                  >
                    <i className={`fa ${config.icon}`} style={{ color: config.iconColor }} />
                  </div>
                  <div>
                    <h5 className='tw:mb-1 tw:font-semibold tw:text-sm'>
                      {insight.title}
                    </h5>
                    <p className='tw:text-muted-foreground tw:mb-0 tw:text-xs'>
                      {insight.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
